import React, { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useOnboardingStep, useHeartbeat } from '../../hooks/useOnboarding'
import {
  getStepForRoute,
  getRouteForStep,
  getStepDisplayName,
  validateOnboardingRoute,
} from '../../utils/onboardingRoutes'
import { Button } from '../ui/Button'
import { Spinner } from '../ui/Spinner'
import type { StepData } from '../../services/onboardingService'

interface OnboardingStepProps {
  children: React.ReactNode
  onComplete?: (result: any) => void
  className?: string
}

const OnboardingStep: React.FC<OnboardingStepProps> = ({
  children,
  onComplete,
  className = '',
}) => {
  const location = useLocation()
  const navigate = useNavigate()
  const stepKey = getStepForRoute(location.pathname)

  const { currentStep, isCurrentStep, progress, completeStep } =
    useOnboardingStep(stepKey || '')

  // Mantener actividad mientras está en un paso
  useHeartbeat()

  // Validar que la ruta sea accesible
  useEffect(() => {
    if (!stepKey) {
      navigate('/onboarding')
      return
    }

    const validation = validateOnboardingRoute(location.pathname, progress)
    if (!validation.isValid && validation.suggestedRoute) {
      navigate(validation.suggestedRoute)
      return
    }
  }, [stepKey, location.pathname, progress, navigate])

  const handleComplete = async (
    stepDataParam: StepData = {},
    notes?: string
  ) => {
    if (!stepKey) return

    try {
      const success = await completeStep(stepDataParam, notes)

      // Llamar callback personalizado si se proporciona
      if (onComplete) {
        onComplete({ success })
      }

      // Si se completó exitosamente, navegar al siguiente paso
      if (success && progress?.currentStepKey) {
        // Refrescar progreso y navegar
        window.location.reload() // Temporal: forzar refresh para obtener nuevo progreso
      }
    } catch (error) {
      console.error('Error completando paso:', error)
    }
  }

  // Navegación entre pasos
  const navigateToNextStep = () => {
    if (!progress?.steps) return

    const currentIndex = progress.steps.findIndex(
      step => step.stepDefinition.stepKey === stepKey
    )
    const nextStep = progress.steps[currentIndex + 1]

    if (nextStep) {
      const nextRoute = getRouteForStep(nextStep.stepDefinition.stepKey, progress?.flow?.variant)
      navigate(nextRoute)
    }
  }

  const navigateToPreviousStep = () => {
    if (!progress?.steps) return

    const currentIndex = progress.steps.findIndex(
      step => step.stepDefinition.stepKey === stepKey
    )
    const previousStep = progress.steps[currentIndex - 1]

    if (previousStep) {
      const previousRoute = getRouteForStep(previousStep.stepDefinition.stepKey, progress?.flow?.variant)
      navigate(previousRoute)
    }
  }

  if (!stepKey) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Paso no encontrado</p>
          <Button onClick={() => navigate('/onboarding')}>
            Ver Progreso General
          </Button>
        </div>
      </div>
    )
  }

  if (!currentStep) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-gray-600">Cargando paso...</p>
        </div>
      </div>
    )
  }

  const stepProgress = progress?.steps || []
  const currentStepIndex = stepProgress.findIndex(
    step => step.stepDefinition.stepKey === stepKey
  )
  const totalSteps = stepProgress.length
  const completedSteps = stepProgress.filter(
    step => step.status === 'completed'
  ).length

  return (
    <div className={`min-h-screen bg-gray-50 ${className}`}>
      {/* Header del paso */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-2 text-sm text-gray-500 mb-1">
                <span>Etapa {currentStep.stepDefinition.stage}</span>
                <span>•</span>
                <span>
                  Paso {currentStep.stepDefinition.stepOrder} de {totalSteps}
                </span>
                <span>•</span>
                <span>{completedSteps} completados</span>
              </div>
              <h1 className="text-2xl font-bold text-gray-900">
                {getStepDisplayName(stepKey)}
              </h1>
              {currentStep.stepDefinition.estimatedTimeMinutes && (
                <p className="text-sm text-gray-600 mt-1">
                  Tiempo estimado: {currentStep.stepDefinition.estimatedTimeMinutes} minutos
                </p>
              )}
            </div>

            <div className="text-right">
              <div className="text-sm text-gray-500 mb-1">Progreso general</div>
              <div className="text-2xl font-bold text-blue-600">
                {progress?.completionPercentage.toFixed(1)}%
              </div>
            </div>
          </div>

          {/* Barra de progreso */}
          <div className="mt-4">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep.stepDefinition.stepOrder / totalSteps) * 100}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Contenido del paso */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6">
            {/* Información del paso */}
            {((currentStep.totalTimeSpentSeconds ?? 0) > 0 ||
              (currentStep.visitCount ?? 0) > 0) && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between text-sm">
                  {(currentStep.totalTimeSpentSeconds ?? 0) > 0 && (
                    <span className="text-blue-700">
                      ⏱️ Tiempo invertido: {Math.round((currentStep.totalTimeSpentSeconds ?? 0) / 60)} min
                    </span>
                  )}
                  {(currentStep.visitCount ?? 0) > 0 && (
                    <span className="text-blue-700">
                      👁️ Visitas: {currentStep.visitCount}
                    </span>
                  )}
                  {currentStep.status === 'completed' && (
                    <span className="text-green-700">
                      ✅ Completado
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Renderizar el componente hijo con props adicionales */}
            {React.cloneElement(children as React.ReactElement, {
              onComplete: handleComplete,
              currentStep: currentStep,
              isCurrentStep: isCurrentStep,
              stepKey: stepKey,
            })}
          </div>
        </div>

        {/* Navegación del paso */}
        <div className="mt-6 flex items-center justify-between">
          <div className="flex space-x-3">
            <Button variant="outline" onClick={() => navigate('/onboarding')}>
              Ver Progreso General
            </Button>

            {currentStepIndex > 0 && (
              <Button variant="outline" onClick={navigateToPreviousStep}>
                ← Paso Anterior
              </Button>
            )}
          </div>

          <div className="flex space-x-3">
            {currentStep.status === 'completed' &&
              currentStepIndex < totalSteps - 1 && (
                <Button variant="outline" onClick={navigateToNextStep}>
                  Siguiente Paso →
                </Button>
              )}

            {currentStep.status === 'completed' &&
              currentStepIndex === totalSteps - 1 && (
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="bg-green-600 hover:bg-green-700"
                >
                  Ir al Dashboard 🎉
                </Button>
              )}
          </div>
        </div>

        {/* Indicador de paso completado */}
        {currentStep.status === 'completed' && (
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <span className="text-2xl mr-3">✅</span>
              <div>
                <h3 className="font-semibold text-green-800">
                  ¡Paso completado!
                </h3>
                <p className="text-green-700 text-sm">
                  Puedes revisar o modificar este paso, o continuar con el
                  siguiente.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Ayuda contextual */}
        <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="flex items-start">
            <span className="text-xl mr-3">💡</span>
            <div>
              <h3 className="font-medium text-gray-800 mb-1">
                ¿Necesitas ayuda?
              </h3>
              <p className="text-gray-600 text-sm mb-2">
                Si tienes dudas sobre este paso, puedes contactar a nuestro
                equipo de soporte.
              </p>
              <Button variant="outline" size="sm">
                Contactar Soporte
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default OnboardingStep
